/**
 * Offline Photos — IndexedDB-backed blob storage for photos captured offline.
 *
 * Photos are stored as Blobs (not base64) for storage efficiency.
 * Linked to offlineQueue entries via queue_entry_id.
 * Max total storage: 50MB (warned at 40MB via StorageManager.estimate()).
 */
import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'bg-project-tracker-offline';
const DB_VERSION = 2; // v2: consolidated — both offlineQueue + offlinePhotos stores created here
const STORE_NAME = 'offlinePhotos';

const MAX_STORAGE_BYTES = 50 * 1024 * 1024; // 50MB
const WARN_STORAGE_BYTES = 40 * 1024 * 1024; // 40MB

export interface OfflinePhoto {
  /** Client-generated UUID */
  id: string;
  /** Links to offlineQueue entry for the site visit */
  queue_entry_id: string;
  blob: Blob;
  filename: string;
  size: number;
  mimetype: string;
  caption: string | null;
  captured_at: number; // Unix ms
}

export interface StorageStatus {
  usedBytes: number;
  quotaBytes: number;
  isNearLimit: boolean;
  isOverLimit: boolean;
}

let _db: IDBPDatabase | null = null;

async function getDb(): Promise<IDBPDatabase> {
  if (_db) return _db;
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // offlineQueue store — must be created in same DB version to avoid NotFoundError
      if (!db.objectStoreNames.contains('offlineQueue')) {
        const queueStore = db.createObjectStore('offlineQueue', { keyPath: 'id' });
        queueStore.createIndex('status', 'status');
        queueStore.createIndex('visit_id', 'visit_id');
        queueStore.createIndex('offline_timestamp', 'offline_timestamp');
      }
      // offlinePhotos store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('queue_entry_id', 'queue_entry_id');
        store.createIndex('captured_at', 'captured_at');
      }
    },
  });
  return _db;
}

export const offlinePhotos = {
  async add(photo: OfflinePhoto): Promise<void> {
    const db = await getDb();
    await db.add(STORE_NAME, photo);
  },

  async getByQueueEntry(queueEntryId: string): Promise<OfflinePhoto[]> {
    const db = await getDb();
    return db.getAllFromIndex(STORE_NAME, 'queue_entry_id', queueEntryId);
  },

  async getAll(): Promise<OfflinePhoto[]> {
    const db = await getDb();
    const all = await db.getAll(STORE_NAME);
    return all.sort((a, b) => a.captured_at - b.captured_at);
  },

  async delete(id: string): Promise<void> {
    const db = await getDb();
    await db.delete(STORE_NAME, id);
  },

  async deleteByQueueEntry(queueEntryId: string): Promise<void> {
    const db = await getDb();
    const photos = await db.getAllFromIndex(STORE_NAME, 'queue_entry_id', queueEntryId);
    for (const photo of photos) {
      await db.delete(STORE_NAME, photo.id);
    }
  },

  async clearAll(): Promise<void> {
    const db = await getDb();
    await db.clear(STORE_NAME);
  },

  async getTotalSize(): Promise<number> {
    const all = await offlinePhotos.getAll();
    return all.reduce((sum, p) => sum + p.size, 0);
  },

  async getStorageStatus(): Promise<StorageStatus> {
    let usedBytes = 0;
    let quotaBytes = MAX_STORAGE_BYTES;

    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      usedBytes = estimate.usage ?? 0;
      quotaBytes = estimate.quota ?? MAX_STORAGE_BYTES;
    } else {
      usedBytes = await offlinePhotos.getTotalSize();
    }

    return {
      usedBytes,
      quotaBytes,
      isNearLimit: usedBytes >= WARN_STORAGE_BYTES,
      isOverLimit: usedBytes >= MAX_STORAGE_BYTES,
    };
  },

  /**
   * Delete oldest photos until under the limit. Returns number of photos deleted.
   */
  async pruneOldestPhotos(targetBytes: number = WARN_STORAGE_BYTES): Promise<number> {
    const db = await getDb();
    const all = await db.getAllFromIndex(STORE_NAME, 'captured_at') as OfflinePhoto[];
    all.sort((a, b) => a.captured_at - b.captured_at); // oldest first

    let deleted = 0;
    let currentSize = all.reduce((s, p) => s + p.size, 0);

    for (const photo of all) {
      if (currentSize <= targetBytes) break;
      await db.delete(STORE_NAME, photo.id);
      currentSize -= photo.size;
      deleted++;
    }
    return deleted;
  },
};
